import React, { useEffect, useState } from "react";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useI18n, formatMoney } from "../i18n";
import { PageHeader, Card, Badge } from "../components/shared";
import {
  Users, CalendarDays, Wallet, AlertTriangle, Pill, PackageX, PackageMinus,
  TrendingUp, Clock, Stethoscope, UserPlus, ArrowDownRight, CreditCard,
  Smartphone, Award, Activity, Sun, Moon, Cloud,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, sub, tone = "brand", testid }) {
  const tones = {
    brand: "bg-brand/10 text-brand",
    accent: "bg-terracotta/10 text-terracotta",
    warning: "bg-[#e3a03b]/15 text-[#8a5a10]",
    danger: "bg-[#d34044]/10 text-[#b12e31]",
    success: "bg-[#2d8a56]/10 text-[#2d8a56]",
  };
  return (
    <Card className="p-5 sm:p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tones[tone]}`}>
          <Icon className="w-5 h-5" strokeWidth={2} />
        </div>
      </div>
      <div className="text-sm text-ink-muted font-medium">{label}</div>
      <div className="mt-1 stat-number text-2xl sm:text-3xl font-bold text-ink tracking-tight">{value}</div>
      {sub && <div className="mt-1 text-xs text-ink-muted">{sub}</div>}
    </Card>
  );
}

function getGreeting(name, lang) {
  const h = new Date().getHours();
  let icon = Sun;
  let key;
  if (h < 12) { key = lang === "fr" ? "Bonjour" : "Good morning"; icon = Sun; }
  else if (h < 18) { key = lang === "fr" ? "Bon après-midi" : "Good afternoon"; icon = Cloud; }
  else { key = lang === "fr" ? "Bonsoir" : "Good evening"; icon = Moon; }
  return { greeting: `${key} ${name || ""}`.trim(), Icon: icon };
}

function timeAgo(iso, lang = "fr") {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return lang === "fr" ? "à l'instant" : "just now";
  if (diff < 3600) return lang === "fr" ? `il y a ${Math.floor(diff / 60)} min` : `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return lang === "fr" ? `il y a ${Math.floor(diff / 3600)} h` : `${Math.floor(diff / 3600)}h ago`;
  return lang === "fr" ? `il y a ${Math.floor(diff / 86400)} j` : `${Math.floor(diff / 86400)}d ago`;
}

const ACTIVITY_META = {
  consultation: { icon: Stethoscope, tone: "bg-brand/10 text-brand" },
  payment: { icon: Wallet, tone: "bg-[#2d8a56]/10 text-[#2d8a56]" },
  stock_out: { icon: ArrowDownRight, tone: "bg-terracotta/10 text-terracotta" },
  patient: { icon: UserPlus, tone: "bg-[#2563eb]/10 text-[#2563eb]" },
};

const PAYMENT_METHOD_ICON = {
  cash: Wallet, card: CreditCard, wave: Smartphone, orange_money: Smartphone,
};

export default function Dashboard() {
  const { t, lang } = useI18n();
  const { user, tenant } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);

  const cur = tenant?.currency || "XOF";
  const { greeting, Icon: GIcon } = getGreeting(user?.first_name, lang);

  const rev = data?.revenue_breakdown || { consultations: 0, medications: 0, other: 0 };
  const revTotal = (rev.consultations || 0) + (rev.medications || 0) + (rev.other || 0);
  const pct = (v) => (revTotal > 0 ? (v / revTotal) * 100 : 0);

  return (
    <div className="animate-fade-in">
      {/* HEADER */}
      <header className="mb-8">
        <div className="flex items-center gap-2 text-ink-muted mb-1">
          <GIcon className="w-4 h-4" />
          <span className="text-sm font-medium">{lang === "fr" ? "Bienvenue" : "Welcome back"}</span>
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-ink tracking-tight" data-testid="dashboard-greeting">
          {greeting} <span className="inline-block">👋</span>
        </h1>
        <p className="mt-1 text-ink-muted">
          {t("dashboard.title")} — <span className="font-semibold text-ink">{tenant?.name}</span>
        </p>
      </header>

      {/* TOP METRICS */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard testid="stat-patients-today" icon={Users} label={t("dashboard.patientsToday")} value={data?.patients_today ?? "-"} sub={`${data?.total_patients ?? 0} ${lang === "fr" ? "au total" : "total"}`} tone="brand" />
        <StatCard testid="stat-apps-today" icon={CalendarDays} label={t("dashboard.appointmentsToday")} value={data?.appointments_today ?? "-"} tone="accent" />
        <StatCard testid="stat-revenue" icon={Wallet} label={t("dashboard.revenueToday")} value={formatMoney(data?.revenue_today || 0, cur)} tone="success" />
        <StatCard testid="stat-unpaid" icon={AlertTriangle} label={t("dashboard.unpaid")} value={formatMoney(data?.unpaid?.total || 0, cur)} sub={`${data?.unpaid?.count || 0} ${lang === "fr" ? "facture(s)" : "invoice(s)"}`} tone="warning" />
      </section>

      {/* MAIN GRID */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Breakdown */}
        <Card className="p-6 lg:col-span-2" data-testid="card-revenue-breakdown">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-heading font-bold text-lg text-ink flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-brand" />
              {lang === "fr" ? "Répartition des revenus du jour" : "Today's revenue breakdown"}
            </h3>
            <span className="stat-number font-bold text-ink">{formatMoney(revTotal, cur)}</span>
          </div>
          {revTotal === 0 ? (
            <div className="py-8 text-center text-ink-muted text-sm">{lang === "fr" ? "Aucun revenu enregistré aujourd'hui." : "No revenue recorded today."}</div>
          ) : (
            <div className="space-y-4">
              <RevRow label={lang === "fr" ? "Consultations" : "Consultations"} amount={rev.consultations} pct={pct(rev.consultations)} cur={cur} color="bg-brand" />
              <RevRow label={lang === "fr" ? "Médicaments" : "Medications"} amount={rev.medications} pct={pct(rev.medications)} cur={cur} color="bg-terracotta" />
              {rev.other > 0 && <RevRow label={lang === "fr" ? "Autres" : "Other"} amount={rev.other} pct={pct(rev.other)} cur={cur} color="bg-[#2563eb]" />}
              {/* Stacked bar */}
              <div className="mt-4 h-3 w-full rounded-full bg-subtle overflow-hidden flex">
                <div style={{ width: `${pct(rev.consultations)}%` }} className="bg-brand h-full" />
                <div style={{ width: `${pct(rev.medications)}%` }} className="bg-terracotta h-full" />
                <div style={{ width: `${pct(rev.other)}%` }} className="bg-[#2563eb] h-full" />
              </div>
            </div>
          )}
        </Card>

        {/* Top Doctors */}
        <Card className="p-6" data-testid="card-top-doctors">
          <h3 className="font-heading font-bold text-lg text-ink flex items-center gap-2 mb-5">
            <Award className="w-5 h-5 text-terracotta" />
            {lang === "fr" ? "Praticiens les plus actifs" : "Top doctors"}
          </h3>
          {data?.top_doctors?.length ? (
            <ul className="space-y-3">
              {data.top_doctors.map((d, i) => (
                <li key={d.doctor_id} className="flex items-center gap-3" data-testid={`top-doctor-${i}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center font-heading font-bold text-sm
                    ${i === 0 ? "bg-brand text-white" : "bg-brand/10 text-brand"}`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-ink text-sm truncate">{d.name}</div>
                    {d.specialty && <div className="text-xs text-ink-muted truncate">{t(`specialties.${d.specialty}`)}</div>}
                  </div>
                  <div className="text-right">
                    <div className="stat-number font-bold text-ink">{d.consultations}</div>
                    <div className="text-[10px] text-ink-muted uppercase tracking-wider">{lang === "fr" ? "consult." : "visits"}</div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="py-8 text-center text-ink-muted text-sm">{lang === "fr" ? "Aucune consultation récente." : "No recent consultations."}</div>
          )}
        </Card>

        {/* Top Medications */}
        <Card className="p-6 lg:col-span-2" data-testid="card-top-meds">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-heading font-bold text-lg text-ink flex items-center gap-2">
              <Pill className="w-5 h-5 text-brand" />
              {t("dashboard.topMeds")}
            </h3>
            <span className="text-xs text-ink-muted font-medium">{lang === "fr" ? "30 derniers jours" : "Last 30 days"}</span>
          </div>
          {data?.top_medications?.length ? (
            <ul className="divide-y divide-[var(--border-default)]">
              {data.top_medications.map((m, i) => {
                const max = data.top_medications[0]?.quantity || 1;
                const pct = (m.quantity / max) * 100;
                return (
                  <li key={m.product_id} className="py-3" data-testid={`top-med-${i}`}>
                    <div className="flex items-center gap-3 mb-1">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-heading font-bold text-sm
                        ${i === 0 ? "bg-brand text-white" : "bg-brand/10 text-brand"}`}>
                        {i + 1}
                      </div>
                      <div className="font-semibold text-ink flex-1 truncate">{m.name}</div>
                      <div className="stat-number text-base font-bold text-ink">
                        {m.quantity} <span className="text-xs font-normal text-ink-muted">{m.unit}</span>
                      </div>
                    </div>
                    <div className="ml-11 h-2 rounded-full bg-subtle overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-brand to-brand-hover" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="ml-11 mt-1 text-xs text-ink-muted">
                      {m.uses} {lang === "fr" ? `utilisation${m.uses > 1 ? "s" : ""}` : `use${m.uses > 1 ? "s" : ""}`}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="py-10 text-center text-ink-muted text-sm">{t("common.noData")}</div>
          )}
        </Card>

        {/* Stock Alerts (DETAILED) */}
        <Card className="p-6" data-testid="card-stock-alerts">
          <h3 className="font-heading font-bold text-lg text-ink flex items-center gap-2 mb-5">
            <AlertTriangle className="w-5 h-5 text-terracotta" />
            {t("dashboard.stockAlerts")}
          </h3>
          <div className="space-y-2">
            {data?.stock_alerts?.out_of_stock?.map((p) => (
              <div key={p.id} className="flex items-center justify-between p-3 rounded-xl bg-[#d34044]/10 border border-[#d34044]/20" data-testid={`alert-out-${p.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <PackageX className="w-4 h-4 text-[#b12e31] shrink-0" />
                  <span className="text-sm font-semibold text-ink truncate">{p.name}</span>
                </div>
                <span className="text-xs font-bold text-[#b12e31] uppercase">{lang === "fr" ? "Rupture" : "Out"}</span>
              </div>
            ))}
            {data?.stock_alerts?.low_stock?.map((p) => (
              <div key={p.id} className="flex items-center justify-between p-3 rounded-xl bg-[#e3a03b]/10 border border-[#e3a03b]/30" data-testid={`alert-low-${p.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <PackageMinus className="w-4 h-4 text-[#8a5a10] shrink-0" />
                  <span className="text-sm font-semibold text-ink truncate">{p.name}</span>
                </div>
                <span className="text-xs font-bold text-[#8a5a10] stat-number">
                  {p.stock_quantity} {p.unit}
                </span>
              </div>
            ))}
            {data?.stock_alerts?.expiring_soon?.map((l) => (
              <div key={l.lot_id} className="flex items-center justify-between p-3 rounded-xl bg-[#2563eb]/10 border border-[#2563eb]/20" data-testid={`alert-exp-${l.lot_id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Clock className="w-4 h-4 text-[#1e40af] shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-ink truncate">{l.product_name}</div>
                    <div className="text-[11px] text-ink-muted font-mono">{l.batch_number}</div>
                  </div>
                </div>
                <span className="text-xs font-bold text-[#1e40af] stat-number">{l.expiration_date}</span>
              </div>
            ))}
            {(!data?.stock_alerts?.out_of_stock?.length && !data?.stock_alerts?.low_stock?.length && !data?.stock_alerts?.expiring_soon?.length) && (
              <div className="p-4 rounded-xl bg-[#2d8a56]/10 border border-[#2d8a56]/20 text-center">
                <div className="text-sm font-semibold text-[#2d8a56]">✓ {lang === "fr" ? "Tout est OK" : "All good"}</div>
              </div>
            )}
          </div>
        </Card>

        {/* Recent activity */}
        <Card className="p-6 lg:col-span-3" data-testid="card-recent-activity">
          <h3 className="font-heading font-bold text-lg text-ink flex items-center gap-2 mb-5">
            <Activity className="w-5 h-5 text-brand" />
            {lang === "fr" ? "Activité récente" : "Recent activity"}
          </h3>
          {data?.recent_activity?.length ? (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.recent_activity.map((a, i) => {
                const meta = ACTIVITY_META[a.type] || ACTIVITY_META.patient;
                const Icon = a.type === "payment" && PAYMENT_METHOD_ICON[a.subtitle]
                  ? PAYMENT_METHOD_ICON[a.subtitle]
                  : meta.icon;
                return (
                  <li key={i} className="flex items-start gap-3 p-3 rounded-xl hover:bg-subtle/50 transition-colors" data-testid={`activity-${i}`}>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${meta.tone}`}>
                      <Icon className="w-4 h-4" strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="font-semibold text-ink text-sm truncate">{a.title}</span>
                        {a.amount != null && (
                          <span className="stat-number font-bold text-[#2d8a56] text-sm whitespace-nowrap">
                            +{formatMoney(a.amount, cur)}
                          </span>
                        )}
                      </div>
                      {a.subtitle && <div className="text-xs text-ink-muted truncate">{a.subtitle}</div>}
                      {a.extra && <div className="text-xs text-ink-muted truncate italic">{a.extra}</div>}
                    </div>
                    <span className="text-xs text-ink-faint whitespace-nowrap">{timeAgo(a.at, lang)}</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="py-10 text-center text-ink-muted text-sm">{t("common.noData")}</div>
          )}
        </Card>
      </section>

      {loading && <div className="mt-6 text-ink-muted text-sm">{t("common.loading")}</div>}
    </div>
  );
}

function RevRow({ label, amount, pct, cur, color }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className={`w-3 h-3 rounded-full ${color}`} />
          <span className="text-sm font-semibold text-ink">{label}</span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="stat-number font-bold text-ink">{formatMoney(amount, cur)}</span>
          <span className="text-xs text-ink-muted stat-number">{pct.toFixed(0)}%</span>
        </div>
      </div>
    </div>
  );
}
